/**
 * UtilizationChart
 *
 * Stacked bar chart showing Hourglass hours vs Other hours per attorney.
 * Sorted by utilization_pct descending. Each bar is one attorney with
 * Hourglass (brand green) stacked on top of Other (gray).
 * The timeframe slider lives in the card header.
 */

import { useMemo } from "react";
import {
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { AttorneyUtilization } from "../../types/dashboard";
import { UTILIZATION_COLORS, CHART_COLORS, CHART_CONFIG } from "../../config/dashboardTheme";
import TimeframeDropdown from "./TimeframeDropdown";

interface UtilizationChartProps {
  attorneys: AttorneyUtilization[];
  timeframe: string;
  onTimeframeChange: (value: string) => void;
}

interface ChartRow {
  name: string;
  fullName: string;
  hourglass: number;
  other: number;
  total: number;
  utilization_pct: number;
}

export default function UtilizationChart({
  attorneys,
  timeframe,
  onTimeframeChange,
}: UtilizationChartProps) {
  const chartData = useMemo<ChartRow[]>(() => {
    return attorneys.map((a) => {
      const other = Math.max(0, a.total_billing_hours - a.hourglass_hours);
      const short =
        a.prof_name.length > 12
          ? a.prof_name.slice(0, 11) + "\u2026"
          : a.prof_name;
      return {
        name: short,
        fullName: a.prof_name,
        hourglass: a.hourglass_hours,
        other,
        total: a.total_billing_hours,
        utilization_pct: a.utilization_pct,
      };
    });
  }, [attorneys]);

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: ChartRow }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-sm">
          <p className="font-semibold mb-2">{data.fullName}</p>
          <div className="space-y-1">
            <p style={{ color: UTILIZATION_COLORS.hourglass }} className="font-medium">
              Hourglass: {data.hourglass.toFixed(1)} hrs
            </p>
            <p className="text-muted-foreground">
              Total Billing: {data.total.toFixed(1)} hrs
            </p>
            <p className="font-medium">
              Utilization: {data.utilization_pct}%
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-xs p-6">
      {/* Card header: title left, timeframe slider right */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold">Utilization by Attorney</h3>
        <TimeframeDropdown value={timeframe} onChange={onTimeframeChange} />
      </div>

      {attorneys.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">
          No utilization data available for the selected timeframe.
        </div>
      ) : (
        <div className="w-full h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={CHART_COLORS.grid.line}
                className="opacity-50"
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: CHART_COLORS.grid.axis }}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
                axisLine={{ stroke: CHART_COLORS.grid.axis, strokeWidth: 1 }}
                tickLine={false}
              />
              <YAxis
                label={{
                  value: "Hours",
                  angle: -90,
                  position: "insideLeft",
                  style: {
                    textAnchor: "middle",
                    fill: CHART_COLORS.grid.axis,
                    fontSize: 12,
                  },
                }}
                tick={{ fontSize: 12, fill: CHART_COLORS.grid.axis }}
                axisLine={{ stroke: CHART_COLORS.grid.axis, strokeWidth: 1 }}
                tickLine={{ stroke: CHART_COLORS.grid.axis }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                height={36}
                content={({ payload }) => (
                  <div className="flex justify-center gap-6 text-sm">
                    {payload?.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-xs"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-muted-foreground">
                          {entry.value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              />
              <Bar
                dataKey="hourglass"
                name="Hourglass"
                stackId="stack"
                fill={UTILIZATION_COLORS.hourglass}
                maxBarSize={CHART_CONFIG.maxBarSize}
                radius={CHART_CONFIG.bottomBarRadius}
              />
              <Bar
                dataKey="other"
                name="Other"
                stackId="stack"
                fill={UTILIZATION_COLORS.other}
                maxBarSize={CHART_CONFIG.maxBarSize}
                radius={CHART_CONFIG.topBarRadius}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
